import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const defaultMetadata = {
    "title": "The Simulacrum | Creative Media Production & Development",
    "description": "Creative direction, websites, applications, media production and interactive experiences for businesses, agencies and cultural organizations. Start a project with The Simulacrum."
};

const routeMetadata = {
    '/': defaultMetadata,
    '/about': {
        title: "About The Simulacrum | Creative Media Production & Development",
        description: "Kyle Hinton’s practice connects creative direction, software development, media production, performance and education, with specialist Solana and Web3 projects."
    },
    '/glossary': {
        title: 'Web3 Glossary | Simulacrum Images',
        description: 'Explore approachable definitions for web3, blockchain, token, NFT, and digital ownership concepts.'
    },
    '/gm-blockchain': {
        title: 'GM Solana Blockchain Book | Simulacrum Images',
        description: 'Discover GM Solana, an introduction to blockchain and NFTs for artists and creators.'
    },
    '/gm-web3-course': {
        title: 'GM Web3 Course | Simulacrum Images',
        description: 'Learn about digital ownership and creating art on-chain through the GM Web3 course from Simulacrum Images.'
    },
    '/articles': {
        title: 'Articles | Simulacrum Images',
        description: 'Read articles from Simulacrum Images about web3, creative technology, blockchain, digital art, and interactive media.'
    },
    '/articles/:id': {
        title: 'Article | Simulacrum Images',
        description: 'Read an article from Simulacrum Images about web3, creative technology, and digital media.'
    },
    '/mimesis-castle': {
        title: 'Mimesis Castle | Simulacrum Images',
        description: 'Explore Mimesis Castle, a web3 digital collectible variety show curated by Simulations.'
    },
    '/services': {
        title: "Creative Media & Development Services | The Simulacrum",
        description: "Commission websites, applications, prototypes, creative media, campaigns and interactive experiences. Explore development capabilities, selected work and bespoke project scoping."
    },
    '/contact': {
        title: "Start a Project | The Simulacrum",
        description: "Email a brief or question to kyle-hinton@outlook.com, or book an introductory project conversation about creative production, development and technical support."
    },
    '/galleries': {
        title: 'Galleries and Collections | Simulacrum Images',
        description: 'Explore digital artworks, collaborations, interactive projects, and on-chain collections from Simulacrum Images.'
    },
    '/god-of-thunder': {
        title: 'God of Thunder Game | Simulacrum Images',
        description: 'Discover God of Thunder, a storm-powered arcade game available on Android and iOS.'
    },
    '/bunny-runner': {
        title: 'Bunny Runner Game | Simulacrum Images',
        description: 'Play Bunny Runner, a fast-paced arcade game available in browsers and on Android and iOS.'
    },
    '/suspicious-microwave-td': {
        title: 'Suspicious Microwave TD | Simulacrum Images',
        description:
            'Suspicious Microwave TD game page from Simulacrum Images.'
    },
    '/suspicious-microwave-terms-of-service': {
        title: 'Suspicious Microwave TD Terms of Use | Simulacrum Images',
        description:
            'Terms of Use for Suspicious Microwave TD, including game accounts, wallet connections, digital assets, purchases, rewards, and service rules.'
    },
    '/suspicious-microwave-privacy-policy': {
        title: 'Suspicious Microwave TD Privacy Policy | Simulacrum Images',
        description:
            'Privacy Policy for Suspicious Microwave TD, including game accounts, wallet connections, purchase verification, digital assets, and player data.'
    },
    '/terms': {
        title: 'Terms of Use | Simulacrum Images',
        description: 'Read the terms governing use of The Crumb Explorer and related Simulacrum Images services.'
    },
    '/privacy': {
        title: 'Privacy Policy | Simulacrum Images',
        description: 'Read the privacy policy for The Crumb Explorer and related Simulacrum Images services.'
    },
    '/copyright': {
        title: 'Copyright Policy | Simulacrum Images',
        description: 'Read copyright information for The Crumb Explorer and related Simulacrum Images services.'
    }
};

const setMeta = (attribute, key, content) => {
    let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }

    element.setAttribute('content', content);
};

const RouteMetadata = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const normalizedPath = pathname !== '/' ? pathname.replace(/\/$/, '') : pathname;
        const metadataPath = normalizedPath.startsWith('/articles/') ? '/articles/:id' : normalizedPath;
        const metadata = routeMetadata[metadataPath] || defaultMetadata;
        const siteUrl = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
        const canonicalUrl = `${siteUrl}${normalizedPath}`;
        const socialImageUrl = `${siteUrl}/images/simulacrum-social-preview.png`;
        const socialImageAlt = 'Pixel-art television with colourful test bars on a dark blue background.';

        document.title = metadata.title;
        setMeta('name', 'description', metadata.description);
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:site_name', 'The Simulacrum');
        setMeta('property', 'og:title', metadata.title);
        setMeta('property', 'og:description', metadata.description);
        setMeta('property', 'og:url', canonicalUrl);
        setMeta('property', 'og:image', socialImageUrl);
        setMeta('property', 'og:image:secure_url', socialImageUrl);
        setMeta('property', 'og:image:type', 'image/png');
        setMeta('property', 'og:image:width', '1100');
        setMeta('property', 'og:image:height', '800');
        setMeta('property', 'og:image:alt', socialImageAlt);
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', metadata.title);
        setMeta('name', 'twitter:description', metadata.description);
        setMeta('name', 'twitter:image', socialImageUrl);
        setMeta('name', 'twitter:image:alt', socialImageAlt);

        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', canonicalUrl);
    }, [pathname]);

    return null;
};

export default RouteMetadata;
